import { useAccount } from '@/account/useAccount';

/**
 * Hook to get access to the current user's API access and usage details based on their subscription.
 *
 * This hook abstracts the logic for determining the user's subscription type, report limits,
 * credits limits, and whether they have access to the JSON API.
 *
 * @returns {object} An object containing:
 * - currentUserHasApiAccess: Function that determines if the user has access to the API, staff and users in a API plan have this.
 * - currentUserHasJsonApiAccess: Function that determines if the user has access to JSON API.
 * - currentApiPlanMonthlyUsageDescription: Function that provides a string description of the user's API plan usage.
 * - currentUserMonthlyReportLimits: Object containing details about the user's monthly report and credits limits.
 */
export const useCurrentUserApiAccess = () => {
  const { data: currentUser, isLoading } = useAccount(true);

  const currentUserIsUnlimited = () => {
    return currentUserSubscriptionInfo?.['show_unlimited_reports?'];
  };

  const currentUserSubscriptionInfo = currentUser?.account?.subscription_info;
  const currentUserSubscriptionFeatures =
    currentUserSubscriptionInfo?.subscription_features;

  const baseMonthlyReportLimit =
    currentUserSubscriptionInfo?.monthly_report_limit || 0;
  const baseMonthlyCreditsLimit =
    currentUserSubscriptionFeatures?.api_credits || 0;

  const monthlyCreditsLimit = currentUserIsUnlimited()
    ? 'Unlimited'
    : baseMonthlyCreditsLimit;

  const monthlyReportsLimit = currentUserIsUnlimited()
    ? 'Unlimited'
    : baseMonthlyReportLimit;

  const reportDescription = `${monthlyReportsLimit} PDF reports a month`;
  const creditDescription = `${monthlyCreditsLimit} credits`;

  const currentUserHasJsonApiAccess = () => {
    return Boolean(
      currentUserSubscriptionFeatures?.api_access ||
        currentUser?.account?.staff_info?.staff
    );
  };

  const currentApiPlanMonthlyUsageDescription = () => {
    return currentUserHasJsonApiAccess()
      ? `${creditDescription} ${reportDescription}`
      : reportDescription;
  };

  const currentUserHasApiAccess = () => {
    return (
      currentUser?.account?.subscription_info?.subscription_features
        ?.api_access || currentUser?.account?.staff_info?.staff
    );
  };

  const currentUserMonthlyReportLimits = {
    isUnlimited: currentUserIsUnlimited(),
    monthlyReportsLimit,
    monthlyCreditsLimit,
    monthlyReportsRemaining:
      currentUserSubscriptionInfo?.monthly_reports_remaining,
    monthlyCreditsRemaining:
      currentUserSubscriptionInfo?.monthly_api_credits_remaining,
  };

  return {
    isLoading,
    currentUserHasApiAccess,
    currentUserHasJsonApiAccess,
    currentApiPlanMonthlyUsageDescription,
    currentUserMonthlyReportLimits,
  };
};
